/* eslint-disable max-len */
import React, { Component } from 'react';
import { ISbStoryData } from 'storyblok-js-client';
import { getComponent, blokToComponent } from '../components';
import { GoogleTagManager } from '../components/custom/google-tag-manager';
import { AdobeAnalytics } from '../components/custom/adobe-analytics';
import { EyeAble } from '../components/custom/eye-able';
import { TrackTibAnalytics } from '../components/custom/tib-tracking';
import { LiveChat } from '../components/custom/live-chat';
import { DomService } from '../services/dom';

import {
  GlobalConfigProps,
  StoryblokService,
  NavigationService,
  Language,
  LanguageService,
  PageContextProvider,
  Link,
  CartService,
} from '../services';
import { SEO } from '../components/custom/seo';
import { ScrollTracking } from '../components/custom/scroll-tracking';
import { StoryblokLinkNode } from '../services/navigation/types';
import { OneTrustCookieBanner } from '../components/custom/onetrust-cookie-banner';
import { RocheHeader } from '../components/custom/roche-header';
import { AdobeTracking } from '../tracking';

export interface EntryData {
  breadcrumbs?: StoryblokLinkNode[];
  story?: ISbStoryData;
  links?: Link[];
  environment?: GlobalConfigProps;
  // TODO: fix any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  navigation?: any;
  contact?: ISbStoryData;
  footer?: ISbStoryData;
  onClickNotice?: ISbStoryData;
  languages?: Language[];
  search?: ISbStoryData;
  settings?: ISbStoryData;
  cartCount?: number;
}

interface StoryblokEntryProps {
  pageContext: EntryData;
  location: {
    state?: {
      maskUrl?: string;
    };
  };
}

type StoryblokEntryState = EntryData;

const parseEntryData = ({ pageContext }: StoryblokEntryProps): StoryblokEntryState => {
  const { breadcrumbs, links } = pageContext;
  const story = { ...pageContext.story };
  const footer = { ...pageContext.footer };
  const onClickNotice = { ...pageContext.onClickNotice };
  const search = { ...pageContext.search };
  const navigation = { ...pageContext.navigation };
  const environment = { ...pageContext.environment };

  return {
    breadcrumbs,
    story,
    links,
    footer,
    onClickNotice,
    search,
    navigation,
    environment: DomService.getGlobalConfig(story.uuid, story.lang, environment),
  };
};

const RocheGlobalConfig = getComponent('roche-global-config') as React.ElementType;
const Breadcrumbs = getComponent('roche-breadcrumbs') as React.ElementType;

// eslint-disable-next-line import/no-default-export
export default class StoryblokEntry extends Component<StoryblokEntryProps, StoryblokEntryState> {
  public static getDerivedStateFromProps(
    props: StoryblokEntryProps, state: StoryblokEntryState,
  ): StoryblokEntryState {
    return state.story.uuid !== props.pageContext.story.uuid
      ? parseEntryData(props)
      : null;
  }

  // eslint-disable-next-line class-methods-use-this
  public isNumeric(val) {
    return /^-?\d+$/.test(val);
  }

  public constructor(props: StoryblokEntryProps) {
    super(props);
    this.state = parseEntryData(props);
    this.trackPageView = this.trackPageView.bind(this);
  }

  public async componentDidMount(): Promise<void> {
    this.maskUrl();
    window.addEventListener('rocheLoginComplete', () => StoryblokService.redirect());
    window.addEventListener(CartService.cartUpdateEvent, this.handleCartCount);
    this.trackPageView();

    LanguageService.getLanguages().then((languages) => this.setState({ languages }));
    await this.handleCartCount();
  }

  public async componentWillUnmount(): Promise<void> {
    window.removeEventListener(CartService.cartUpdateEvent, this.handleCartCount);
  }

  private handleCartCount = async (): Promise<void> => {
    const cart = await CartService.getCart();
    this.setState({ ...this.state, cartCount: cart.length });
  };

  public render(): JSX.Element {
    const {
      breadcrumbs,
      story,
      links,
      navigation,
      footer,
      onClickNotice,
      search,
      cartCount,
      environment,
      ...globalConfig
    } = this.state;

    const crisisPageActive = story?.content?.crisis_active;
    const shouldFixHeader = story.content.fixed_header;
    const canShowNavigation = !story?.content?.hide_navigation && navigation && !crisisPageActive;
    const canShowFooter = !story.content.hide_footer;
    const canShowHeader = !story.content.hide_header;
    const canShowStockPrice = !story.content.hide_stock_price;

    const showBreadcrumbs = story.content.show_breadcrumbs && story.content.component !== 'story';
    const enabledCookieBanner = !story.content.disable_cookie_banner;

    return (
      <>
        <PageContextProvider
          story={story}
          resolvedLinks={links}
          cartLink={NavigationService.getParsedNavigationLink(this.state?.navigation?.content?.cart_link)}
          environment={environment}
        >
          { enabledCookieBanner && <OneTrustCookieBanner /> }
          <ScrollTracking data={this.state} />
          <GoogleTagManager
            googleTagManagerId={environment.googleTagManagerId}
          ></GoogleTagManager>
          <EyeAble />
          {/* DUE TO CHANGE */}
          <TrackTibAnalytics fullSlug={story.full_slug} name={story.name} />
          <AdobeAnalytics scriptSrc={environment.adobeAnalytics}></AdobeAnalytics>
          <SEO
            {...story.content.meta_tags}
            pageType={story.content.component}
            lang={story.lang}
            slug={story.full_slug}
            authorized_roles={story.content.authorized_roles}
            robots={story.content.meta_tags_robots}
            cludo={story.content.meta_tags_robots_cludo}
            teaser_image={
              story.content?.teaser_image?.filename
              || story.content?.highlight_image?.filename
              || ''
            }
            teaser_title={
              story.content?.headline
              || story.content?.teaser_title
              || story.content?.highlight_title
              || story.content?.navigation_title
            }
            description={
              story.content.teaser_text || story.content.highlight_description
            }
            first_published_at={story?.first_published_at}
          ></SEO>
          <RocheGlobalConfig {...globalConfig} {...environment}></RocheGlobalConfig>
          {canShowHeader && (
            <RocheHeader
              navigation={navigation?.content}
              showNavigation={canShowNavigation}
              isFixed={shouldFixHeader}
              cart={{
                link: navigation?.content.cart_link,
                count: cartCount,
                maxCount: 9,
              }}/>
          )}
          {showBreadcrumbs && <Breadcrumbs list={breadcrumbs} />}
          {blokToComponent({ blok: story.content, getComponent })}
          {!crisisPageActive
            && canShowFooter
            && footer
            && blokToComponent({
              blok: { ...footer.content, canShowStockPrice },
              getComponent,
            })}
          {onClickNotice.content
            && blokToComponent({ blok: onClickNotice.content, getComponent })}
          <LiveChat />
        </PageContextProvider>
      </>
    );
  }

  private maskUrl(): void {
    // eslint-disable-next-line react/prop-types
    const { maskUrl } = this.props.location.state || {};
    return maskUrl ? window.history.replaceState('', '', maskUrl) : undefined;
  }

  private trackPageView(): void {
    let isInitialConsentTracked = false;
    let debounceTimeout: NodeJS.Timeout | null = null;

    const trackConsent = () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      debounceTimeout = setTimeout(() => {
        if (window.OptanonActiveGroups !== undefined) {
          AdobeTracking(this.state, 'consentUpdated');
        }
      }, 100);
    };

    window.addEventListener('OneTrustGroupsUpdated', trackConsent);

      /* Polling function to check when window.OptanonActiveGroups
    is available before calling AdobeTracking
  */
    const waitForOptanonGroups = (callback: () => void) => {
      const checkOptanonGroups = setInterval(() => {
        if (window.OptanonActiveGroups !== undefined) {
          clearInterval(checkOptanonGroups);
          if (!isInitialConsentTracked) {
            isInitialConsentTracked = true;
            trackConsent();
          }
          callback();
        }
      }, 100);
    };

    waitForOptanonGroups(() => {
      AdobeTracking(this.state, 'defaultPageView');
    });
  }
}
